<template>
     <div class="container-fluid  pt-2">

  <div class="row mb-3">
    <div class="col-12">
      <a href="/customers" class="text-muted small"><i class="bi bi-arrow-left mr-1"></i>Customers</a>
      <h4 class="font-weight-bold text-primary mb-0 mt-1">Customer Profile</h4>
    </div>
  </div>

  <div class="row">
    <!-- Customer Info -->
    <div class="col-md-4 col-12">
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start">
            <h4 class="text-primary font-weight-bold mb-0">{{ customer_data.full_name }}</h4>
            <div class="dropdown">
              <a type="button" class="btn btn-sm px-2 btn-outline-secondary dropdown-toggle" data-toggle="dropdown" aria-expanded="false">Actions</a>
              <div class="dropdown-menu dropdown-menu-right">
                <a v-if="customer_data.phone" class="dropdown-item" :href="'tel:' + customer_data.phone">Call</a>
                <div v-if="customer_data.phone" class="dropdown-divider"></div>
                <a v-if="customer_data.phone" class="dropdown-item" @click="openMessageModal">Send Message</a>
                <div v-if="customer_data.phone" class="dropdown-divider"></div>
                <a v-if="customer_data.email" class="dropdown-item" :href="'mailto:' + customer_data.email">Email</a>
                <div v-if="customer_data.email" class="dropdown-divider"></div>
                <a v-if="customer_data.phone" class="dropdown-item" @click="openStatementModal">Send Statement</a>
                <div v-if="customer_data.phone" class="dropdown-divider"></div>
                <a class="dropdown-item" :href="'/customer-statement/' + customer_data.id" target="_blank" rel="noopener">View Statement</a>
                <div class="dropdown-divider"></div>
                <a class="dropdown-item" @click="EditCustomerModal">Edit</a>
                <div v-if="userDetails.roles?.[0]?.name === 'Admin' || hasPermission('can manage customers')" class="dropdown-divider"></div>
                <a v-if="userDetails.roles?.[0]?.name === 'Admin' || hasPermission('can manage customers')" class="dropdown-item" @click="openMergeModal">Merge Duplicate</a>
                <div v-if="userDetails.roles?.[0]?.name === 'Admin' || hasPermission('can manage customers')" class="dropdown-divider"></div>
                <a v-if="userDetails.roles?.[0]?.name === 'Admin' || hasPermission('can manage customers')" class="dropdown-item text-danger" @click="DeleteCustomer(customer_data.id)">Delete</a>
              </div>
            </div>
          </div>
          <ul class="text-left mt-2">
            <li><strong>Date Added:</strong> {{ formatDate(customer_data.created_at, 'DD-MMM-YYYY') }}</li>
            <li v-if="customer_data.phone"><strong>Phone:</strong> {{ customer_data.phone }}</li>
            <li v-if="customer_data.email"><strong>Email:</strong> {{ customer_data.email }}</li>
            <li v-if="customer_data.company"><strong>Company:</strong> {{ customer_data.company }}</li>
            <li v-if="customer_data.krapin"><strong>KRA PIN:</strong> {{ customer_data.krapin }}</li>
            <li v-if="customer_data.apartment"><strong>House / apartment:</strong> {{ customer_data.apartment }}</li>
            <li v-if="customer_data.address_address"><strong>Street:</strong> {{ customer_data.address_address }}</li>
            <li v-if="customer_data.town"><strong>Town:</strong> {{ customer_data.town }}</li>
            <li v-if="customer_data.address_latitude && customer_data.address_longitude">
              <a :href="'https://www.google.com/maps/search/?api=1&query=' + customer_data.address_latitude + ',' + customer_data.address_longitude" target="_blank" rel="noopener" class="text-primary"><i class="bi bi-geo-alt mr-1"></i>Open location in Maps</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Wallet Card -->
      <div v-if="walletEnabled" class="card mt-3">
        <div class="card-body py-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="font-weight-bold mb-0">
              <i class="bi bi-wallet2 mr-1"></i> Wallet Balance
            </h6>
            <div v-if="hasPermission('subscriptions.manage_wallet') || userDetails.roles?.[0]?.name === 'Admin'">
              <button
                class="btn btn-sm btn-outline-primary mr-1"
                @click="openCreditModal"
              >
                <i class="bi bi-plus-circle mr-1"></i> Add Funds
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="$refs.sharePaymentLink.openForWalletTopup(customer_data)"
              >
                <i class="bi bi-share mr-1"></i> Share Payment Link
              </button>
            </div>
          </div>
          <span v-if="walletLoading" class="text-muted"><small>Loading…</small></span>
          <h3 v-else class="font-weight-bold text-success mb-0">
            {{ convertCurrencyToShillings(walletBalance) }}
          </h3>
        </div>
      </div>
      <!-- Loyalty Card -->
      <div v-if="loyaltyEnabled" class="card mt-3">
        <div class="card-body py-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="font-weight-bold mb-0">
              <i class="bi bi-star mr-1"></i> Loyalty Points
            </h6>
            <button
              v-if="hasPermission('adjust_loyalty_points') || userDetails.roles?.[0]?.name === 'Admin'"
              class="btn btn-sm btn-outline-primary"
              @click="openAdjustModal"
            >
              <i class="bi bi-pencil mr-1"></i> Adjust
            </button>
          </div>
          <span v-if="loyaltyLoading" class="text-muted"><small>Loading…</small></span>
          <template v-else>
            <h3 class="font-weight-bold text-primary mb-0">{{ loyaltyBalance }} pts</h3>
            <small class="text-muted">
              Earned {{ loyaltyLifetimeEarned }} &bull; Redeemed {{ loyaltyLifetimeRedeemed }}
            </small>
          </template>
        </div>
      </div>
      <share-payment-link-modal ref="sharePaymentLink"></share-payment-link-modal>
    </div>

    <!-- Right Column -->
    <div class="col-md-8 col-sm-12">
      <div class="row text-center mb-3">
        <div class="col-sm-4 border">
          <p class="font-weight-bold text-dark py-1">{{ customer_orders.length }}</p>
          <small>Visits</small>
        </div>
        <div class="col-sm-4 border">
          <p class="font-weight-bold text-dark py-1">{{ max_order }}</p>
          <small>Highest Order Amount</small>
        </div>
        <div class="col-sm-4 border">
          <p class="font-weight-bold text-dark py-1">{{ last_visit }}</p>
          <small>Last Visit</small>
        </div>
      </div>

      <!-- Orders Table -->
      <div v-if="orderstable.items.length > 0">

        <!-- Date Range Filter -->
        <div class="row mb-3 align-items-center">
          <div class="col">
            <date-range-picker
              :auto-apply="autoApply"
              v-model="dateRange"
              :ranges="ranges"
              :opens="datepicker_opens"
              :append-to-body="append_to_body"
              class="custom-width-datepicker"
            >
              <template #input="picker">
                <p class="mb-0" v-if="picker.startDate !== null && picker.endDate !== null">
                  {{ formatDate(picker.startDate, 'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate, 'DD-MMM-YYYY') }}
                </p>
                <p class="mb-0" v-else><i class="bi bi-calendar-event"></i> Filter by Date</p>
              </template>
            </date-range-picker>
          </div>
          <div class="col-auto" v-if="dateRange.startDate">
            <a class="btn btn-sm btn-outline-secondary" @click="clearFilter">Clear</a>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="row text-center mb-3" v-if="summary">
          <div class="col-6 col-sm-3 border">
            <p class="font-weight-bold text-dark py-1 mb-0">{{ summary.order_count }}</p>
            <small>Total Orders</small>
          </div>
          <div class="col-6 col-sm-3 border">
            <p class="font-weight-bold text-dark py-1 mb-0">{{ convertCurrencyToShillings(summary.period_paid) }}</p>
            <small>{{ dateRange.startDate ? 'Period Received' : 'Total Received' }}</small>
          </div>
          <div class="col-6 col-sm-3 border">
            <p class="font-weight-bold text-danger py-1 mb-0">{{ convertCurrencyToShillings(summary.period_balance) }}</p>
            <small>Outstanding Balance</small>
          </div>
          <div class="col-6 col-sm-3 border">
            <p class="font-weight-bold text-primary py-1 mb-0">{{ convertCurrencyToShillings(summary.all_time_spend) }}</p>
            <small>All-time Spend</small>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">Recent Transactions</h4>
          <a @click="downloadCustomerReport(customer_data.id)" class="btn btn-outline-secondary btn-sm">
            Download <i class="bi bi-download"></i>
          </a>
        </div>
        <b-table
          sticky-header="100vh"
          hover
          :items="orderstable.items"
          :fields="orderstable.fields"
          sort-icon-left
          responsive="sm"
          @row-clicked="customerTrxRowSelected"
        >
          <template #cell(index)="data">
            <span>{{ data.index + 1 }}</span>
          </template>
          <template #cell(status_label)="data">
            <span
              v-if="data.item.balance > 0"
              class="badge badge-warning text-dark"
              style="font-size:11px; padding:4px 8px;"
            >
              {{ convertCurrencyToShillings(data.item.balance) }} owed
            </span>
            <span
              v-else
              class="badge badge-success"
              style="font-size:11px; padding:4px 8px;"
            >
              Cleared
            </span>
          </template>
        </b-table>
      </div>
      <div v-if="product_insights">
  <h4>Product Insights</h4>
        <div class="d-flex justify-content-between align-items-center">
            <div class="table-responsive">
   <table class="table ">
            <thead>
                <tr>
                    <td>#</td>
                    <td>Item</td>
                    <td>Qty</td>
                    <td>Total Spent</td>
                    <td>Purchase Count</td>
                </tr>
            </thead>
            <tbody>
                <tr v-for="insight in product_insights">
                    <td>#</td>
                    <td>{{ insight.product_name }}</td>
                    <td> {{ insight.total_qty }} </td>
                    <td> {{ insight.total_spent }} </td>
                    <td> {{ insight.purchase_count }} </td>
                    <td> {{ formatDate(insight.last_purchased_at,'h:mm A DD-MM-yyyy') }} </td>

                </tr>
            </tbody>
          </table>
            </div>
            <hr />
            <div class="mb-5"></div>

        </div>

      </div>

      <!-- Payment History -->
      <div v-if="paymentHistory.length" class="mt-4">
        <h4>Payment History</h4>
        <b-table
          :items="paymentHistory"
          :fields="paymentHistoryFields"
          striped
          hover
          small
          responsive="sm"
          @row-clicked="paymentHistoryRowClicked"
        >
          <template #cell(date)="{ item }">
            {{ formatDate(item.date, 'DD-MMM-YYYY HH:mm') }}
          </template>
          <template #cell(amount)="{ item }">
            <span class="text-success font-weight-bold">{{ convertCurrencyToShillings(item.amount) }}</span>
          </template>
          <template #cell(method)="{ item }">
            <span class="badge badge-secondary text-capitalize">{{ item.method }}</span>
          </template>
          <template #cell(order_number)="{ item }">
            <a href="#" @click.prevent="paymentHistoryRowClicked(item)">{{ item.order_number }}</a>
          </template>
        </b-table>
      </div>

      <!-- Wallet Transactions -->
      <div v-if="walletEnabled && walletTransactions.length" class="mt-4">
        <h4>Wallet History</h4>
        <b-table
          :items="walletTransactions"
          :fields="walletTxFields"
          striped
          hover
          small
          responsive="sm"
        >
          <template #cell(created_at)="{ item }">
            {{ formatDate(item.created_at, 'DD-MMM-YYYY HH:mm') }}
          </template>
          <template #cell(type)="{ item }">
            <span :class="['badge', item.type === 'credit' ? 'badge-success' : 'badge-danger']">
              {{ item.type }}
            </span>
          </template>
          <template #cell(amount)="{ item }">
            <span :class="item.type === 'credit' ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
              {{ item.type === 'credit' ? '+' : '−' }}{{ convertCurrencyToShillings(item.amount) }}
            </span>
          </template>
          <template #cell(source)="{ item }">
            <small class="text-muted text-capitalize">{{ (item.source || '—').replace(/_/g, ' ') }}</small>
          </template>
        </b-table>
      </div>

      <!-- No Orders -->
      <div v-else class="d-flex justify-content-center align-items-center" style="height: 300px;">
        <div class="text-center">
          <img src="/images/embarrassed.png" style="width:100px;" class="img-fluid">
          <h6 class="py-3">Customer hasn't made any order</h6>
        </div>
      </div>

      <!-- Loyalty Transactions -->
      <div v-if="loyaltyEnabled && loyaltyTransactions.length" class="mt-4">
        <h4>Loyalty History</h4>
        <b-table
          :items="loyaltyTransactions"
          :fields="loyaltyTxFields"
          striped
          hover
          small
          responsive="sm"
        >
          <template #cell(created_at)="{ item }">
            {{ formatDate(item.created_at, 'DD-MMM-YYYY HH:mm') }}
          </template>
          <template #cell(type)="{ item }">
            <span :class="['badge', loyaltyTypeBadgeClass(item.type)]">{{ item.type }}</span>
          </template>
          <template #cell(points)="{ item }">
            <span :class="item.points >= 0 ? 'text-dark font-weight-bold' : 'text-danger font-weight-bold'">
              {{ item.points >= 0 ? '+' : '' }}{{ item.points }}
            </span>
          </template>
        </b-table>
      </div>
    </div>
  </div>
    <!-- Add Funds to Wallet modal -->
    <b-modal ref="creditWalletModal" title="Add Funds to Wallet" hide-footer>
      <p class="text-muted mb-3">
        Crediting <strong>{{ customer_data.full_name }}</strong>'s wallet.
        A payment record is created for every top-up.
      </p>
      <div class="form-group">
        <label>Amount <span class="text-danger">*</span></label>
        <input
          v-model="creditForm.amount"
          type="number"
          min="1"
          step="0.01"
          class="form-control"
          placeholder="0.00"
        />
      </div>
      <div class="form-group">
        <label>Note <span class="text-muted">(optional)</span></label>
        <input
          v-model="creditForm.description"
          type="text"
          class="form-control"
          placeholder="e.g. Cash received at counter"
        />
      </div>
      <div v-if="creditForm.error" class="alert alert-danger py-2 mb-2">{{ creditForm.error }}</div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.creditWalletModal.hide()">Cancel</button>
        <button
          class="btn btn-primary"
          :disabled="!creditForm.amount || creditForm.loading"
          @click="submitCredit"
        >
          <span v-if="creditForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Add Funds
        </button>
      </div>
    </b-modal>

    <!-- Adjust Loyalty Points modal -->
    <b-modal ref="adjustLoyaltyModal" title="Adjust Loyalty Points" hide-footer>
      <p class="text-muted mb-3">
        Adjusting <strong>{{ customer_data.full_name }}</strong>'s loyalty balance
        (currently {{ loyaltyBalance }} pts). Use a negative number to deduct points.
      </p>
      <div class="form-group">
        <label>Points <span class="text-danger">*</span></label>
        <input
          v-model.number="adjustForm.delta"
          type="number"
          step="1"
          class="form-control"
          placeholder="e.g. 50 or -20"
        />
      </div>
      <div class="form-group">
        <label>Reason <span class="text-danger">*</span></label>
        <input
          v-model="adjustForm.reason"
          type="text"
          class="form-control"
          placeholder="e.g. Goodwill gesture for delayed order"
        />
      </div>
      <div v-if="adjustForm.error" class="alert alert-danger py-2 mb-2">{{ adjustForm.error }}</div>
      <div class="d-flex justify-content-end">
        <button class="btn btn-secondary mr-2" @click="$refs.adjustLoyaltyModal.hide()">Cancel</button>
        <button
          class="btn btn-primary"
          :disabled="!adjustForm.delta || !adjustForm.reason || adjustForm.loading"
          @click="submitLoyaltyAdjust"
        >
          <span v-if="adjustForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          Save Adjustment
        </button>
      </div>
    </b-modal>

    <b-modal ref="mergeModal" title="Merge Duplicate Customer" hide-footer @hidden="resetMerge">
      <p class="text-muted">
        Pick the duplicate record. All its sales, invoices, quotes, payments, subscriptions, wallet and loyalty points
        move to <strong>{{ customer_data.full_name }}</strong>, and the duplicate is removed.
      </p>
      <div v-if="!mergeForm.target">
        <input
          class="form-control"
          type="text"
          v-model="mergeForm.search"
          placeholder="Search name, phone or email..."
          @input="searchMergeCandidates"
        />
        <div class="list-group mt-2">
          <a
            v-for="c in mergeForm.results"
            :key="c.id"
            href="#"
            class="list-group-item list-group-item-action"
            @click.prevent="selectMergeTarget(c)"
          >
            <strong>{{ c.full_name }}</strong>
            <div class="small text-muted">{{ c.phone || '-' }} &bull; {{ c.email || '-' }} &bull; {{ c.visits_count || 0 }} sales</div>
          </a>
        </div>
      </div>
      <div v-else>
        <div class="d-flex justify-content-between align-items-center border rounded p-2">
          <div>
            <strong>{{ mergeForm.target.full_name }}</strong>
            <div class="small text-muted">{{ mergeForm.target.phone || '-' }} &bull; {{ mergeForm.target.email || '-' }}</div>
          </div>
          <button type="button" class="btn btn-link text-secondary" @click="mergeForm.target = null">Change</button>
        </div>
        <div v-if="mergeForm.loadingPreview" class="text-center my-3">
          <span class="spinner-border spinner-border-sm"></span>
        </div>
        <div v-else class="mt-3">
          <p v-if="!Object.keys(mergeForm.counts).length" class="text-muted mb-0">No linked records; the duplicate will just be removed.</p>
          <ul v-else class="list-unstyled mb-0">
            <li v-for="(n, table) in mergeForm.counts" :key="table" class="d-flex justify-content-between">
              <span>{{ table.replace(/_/g, ' ') }}</span>
              <span class="badge badge-secondary">{{ n }}</span>
            </li>
          </ul>
        </div>
        <p class="text-danger mt-3 mb-2">This cannot be undone from the app.</p>
        <button
          type="button"
          class="btn btn-danger btn-block"
          :disabled="mergeForm.merging || mergeForm.loadingPreview"
          @click="confirmMerge"
        >
          <span v-if="mergeForm.merging" class="spinner-border spinner-border-sm mr-1"></span>
          Merge into {{ customer_data.full_name }}
        </button>
      </div>
    </b-modal>

    <b-modal id="message-modal" ref="message_modal" hide-footer>
      <div class="text-center">
        <h2>{{ messageForm.mode === 'statement' ? 'Send Statement' : 'Send Message' }}</h2>
        <p class="text-muted mb-0">To: {{ customer_data.full_name }} &bull; {{ customer_data.phone }}</p>
      </div>
      <div class="form-group mt-3">
        <textarea
          class="form-control"
          rows="5"
          v-model="messageForm.text"
          placeholder="Type your message..."
        ></textarea>
        <div class="d-flex justify-content-between mt-1">
          <small v-if="messageForm.mode === 'statement'">
            <a href="#" @click.prevent="resetMessageToStatement">Reset to default message</a>
          </small>
          <small v-else></small>
          <small class="text-muted">{{ messageForm.text.length }} characters</small>
        </div>
      </div>
      <div class="d-flex mt-2">
        <button
          type="button"
          class="btn btn-outline-success flex-fill mr-2"
          :disabled="!messageForm.text.trim()"
          @click="sendMessageViaWhatsApp"
        >
          <i class="bi bi-whatsapp mr-1"></i> WhatsApp
        </button>
        <button
          type="button"
          class="btn btn-outline-primary flex-fill"
          :disabled="!messageForm.text.trim() || messageForm.loading"
          @click="sendMessageViaSMS"
        >
          <span v-if="messageForm.loading" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-chat-dots mr-1"></i> SMS
        </button>
      </div>
    </b-modal>

    <b-modal ref="edit-customer-modal" hide-footer static>

            <div class="container text-center">
                <div class="text-centre"><h2 class="font-weight-bold ">Update {{customer_data.full_name}}'s info</h2></div>

            </div>
            <div class="text-center" v-html="successMessage">{{successMessage}}</div>
            <div class="container py-3">
                <!-- Actual search box -->
                <form v-on:submit="EditCustomerInfo">
                    <div class="form-row py-1">
                        <div class="col-12">
                            <input class="form-control border-bottom" v-model="customer_data.full_name" name="f_name" type="text" placeholder="First Name"/>


                        </div>

                    </div>
                    <div class="form-group py-1">

                        <input class="form-control border-bottom" name="c_email" v-model="customer_data.email" type="email" placeholder="Email"/>


                    </div>

                    <div class="form-group py-1">

                        <input class="form-control border-bottom" name="krapin" v-model="customer_data.krapin" type="text" placeholder="KRA PIN"/>
                        <input class="form-control border-bottom" name="company" v-model="customer_data.company" type="text" placeholder="Name of Company"/>


                    </div>
                    <div class="form-group">

                        <input class="form-control border-bottom" v-model="customer_data.phone" name="phone_no" type="text" placeholder="Phone Number"/>


                    </div>
                    <div class="form-row py-1">
                        <div class="col-12">
                            <input class="form-control border-bottom" v-model="customer_data.apartment" name="apartment" type="text" placeholder="House / Apartment"/>
                        </div>
                    </div>
                    <div class="form-row py-1">
                        <div class="col-6">
                            <input class="form-control border-bottom" v-model="customer_data.address_address" name="street" type="text" placeholder="Street"/>
                        </div>
                        <div class="col-6">
                            <input class="form-control border-bottom" v-model="customer_data.town" name="town" type="text" placeholder="Town"/>
                        </div>
                    </div>

                    <div class="form-group pt-3">
                        <button class="btn btn-primary btn-block">Update</button>
                    </div>
                </form>
            </div>



        </b-modal>
  </div>


</template>

<script>
import moment from 'moment';
import { mapGetters } from "vuex";
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";

export default {
  name: 'customer-profile',

  components: {
    DateRangePicker,
  },

  data() {
    return {
      customer_data: {},
      product_insights: [],
      customer_orders: [],
      summary: null,
      dateRange: { startDate: null, endDate: null },
      autoApply: true,
      append_to_body: true,
      datepicker_opens: 'right',
      ranges: false,

      successMessage: '',
      max_order: 0,
      last_visit: '',

      paymentHistory: [],
      paymentHistoryFields: [
        { key: 'date',         label: 'Date',   sortable: true },
        { key: 'amount',       label: 'Amount', sortable: true },
        { key: 'method',       label: 'Method', sortable: false },
        { key: 'order_number', label: 'Order',  sortable: false },
      ],

      walletBalance: '0.00',
      walletLoading: false,
      walletTransactions: [],
      walletTxFields: [
        { key: 'created_at',  label: 'Date',   sortable: true  },
        { key: 'type',        label: 'Type',   sortable: false },
        { key: 'amount',      label: 'Amount', sortable: true  },
        { key: 'source',      label: 'Source', sortable: false },
        { key: 'description', label: 'Note',   sortable: false },
      ],
      creditForm: { amount: '', description: '', loading: false, error: '' },
      messageForm: { mode: 'message', text: '', loading: false },
      mergeForm: { search: '', results: [], target: null, counts: {}, loadingPreview: false, merging: false, timer: null },

      loyaltyLoading: false,
      loyaltyBalance: 0,
      loyaltyLifetimeEarned: 0,
      loyaltyLifetimeRedeemed: 0,
      loyaltyTransactions: [],
      loyaltyTxFields: [
        { key: 'created_at',  label: 'Date',        sortable: true  },
        { key: 'type',        label: 'Type',        sortable: false },
        { key: 'points',      label: 'Points',      sortable: true  },
        { key: 'description', label: 'Note',        sortable: false },
      ],
      adjustForm: { delta: '', reason: '', loading: false, error: '' },

      orderstable: {
        fields: [
          "index",
          { key: "order", sortable: true },
          { key: "amount", label: "Amount", sortable: true },
          { key: "status_label", label: "Status", sortable: false },
          { key: "date_", label: "Date", sortable: true },
        ],
        items: []
      },
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    walletEnabled() {
      return !!this.userDetails?.company?.companysettings?.enable_customer_wallet;
    },

    loyaltyEnabled() {
      return !!this.userDetails?.company?.companysettings?.has_loyalty;
    },

    formattedWhatsappPhone() {
      if (!this.customer_data.phone) return '';
      return this.customer_data.phone.startsWith('0')
        ? '254' + this.customer_data.phone.substring(1)
        : this.customer_data.phone.replace(/^\+/, '');
    },

    defaultStatementMessage() {
      if (!this.customer_data.id) return '';
      const url = `${window.location.origin}/customer-statement/${this.customer_data.id}`;
      return `Hello ${this.customer_data.full_name}, please view your statement from ${this.userDetails.company?.name || ''} here: ${url}`;
    },
  },

  watch: {
    dateRange(newVal) {
      if (newVal.startDate && newVal.endDate) {
        this.getcustomer_data();
      }
    },
  },

  mounted() {
    this.getcustomer_data();
  },

  methods: {
    getCustomerIdFromURL() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('id');
    },

    customerTrxRowSelected(item) {
      window.location.replace('/orders?order-number=' + item.orderId + '&source=/customers?cid=');
    },

    paymentHistoryRowClicked(item) {
      if (!item.order_id) return;
      window.location.replace('/orders?order-number=' + item.order_id + '&source=/customers?cid=');
    },

    async getcustomer_data() {
      const customerId = this.getCustomerIdFromURL();

      try {
        const response = await axios.get('/api/customer_details', {
          params: {
            customer_id: customerId,
            start_date: this.dateRange.startDate || undefined,
            end_date: this.dateRange.endDate || undefined,
          }
        });

        const result = response.data;
        this.customer_data = result.customer;
        this.max_order = result.max_order;
        this.customer_orders = result.final_order;
        this.product_insights = result.product_insights;
        this.summary = result.summary;
        this.paymentHistory = result.payment_history || [];

        this.orderstable.items = result.final_order.map(order => ({
          order: order.order,
          orderId: order.id,
          amount: order.total_amount,
          balance: order.balance,
          status: order.status,
          status_label: '',
          date_: moment(order.created_at).format("DD-MM-YYYY hh:mm A")
        }));

        if (result.final_order.length > 0) {
          this.last_visit = moment(result.final_order[0].created_at).fromNow();
        }

        await this.loadWalletData();
        await this.loadLoyaltyData();
      } catch (error) {
        console.error('Error loading customer insights:', error);
      }
    },

    async loadWalletData() {
      if (!this.walletEnabled || !this.customer_data.id) return;
      this.walletLoading = true;
      try {
        const [balRes, txRes] = await Promise.all([
          axios.get(`/api/subscriptions/wallet/${this.customer_data.id}`),
          axios.get(`/api/subscriptions/wallet/${this.customer_data.id}/transactions`, { params: { per_page: 10 } }),
        ]);
        this.walletBalance      = balRes.data.balance;
        this.walletTransactions = txRes.data.data || [];
      } catch {
        // wallet not provisioned yet or permission denied — fail silently
      } finally {
        this.walletLoading = false;
      }
    },

    openCreditModal() {
      this.creditForm = { amount: '', description: '', loading: false, error: '' };
      this.$refs.creditWalletModal.show();
    },

    async submitCredit() {
      this.creditForm.loading = true;
      this.creditForm.error   = '';
      try {
        const { data } = await axios.post(`/api/subscriptions/wallet/${this.customer_data.id}/credit`, {
          amount:      parseFloat(this.creditForm.amount),
          description: this.creditForm.description || undefined,
        });
        this.$refs.creditWalletModal.hide();
        this.$root.$toasts.push({ type: 'success', message: `Wallet credited. New balance: ${data.balance}` });
        await this.loadWalletData();
      } catch (e) {
        this.creditForm.error = e.response?.data?.message || 'Failed to add funds.';
      } finally {
        this.creditForm.loading = false;
      }
    },

    async loadLoyaltyData() {
      if (!this.loyaltyEnabled || !this.customer_data.id) return;
      this.loyaltyLoading = true;
      try {
        const [balRes, txRes] = await Promise.all([
          axios.get(`/api/loyalty/members/${this.customer_data.id}`),
          axios.get(`/api/loyalty/members/${this.customer_data.id}/transactions`, { params: { per_page: 10 } }),
        ]);
        this.loyaltyBalance = balRes.data.balance;
        this.loyaltyLifetimeEarned = balRes.data.lifetime_earned;
        this.loyaltyLifetimeRedeemed = balRes.data.lifetime_redeemed;
        this.loyaltyTransactions = txRes.data.data || [];
      } catch {
        // loyalty not enrolled yet or permission denied — fail silently, same as wallet
      } finally {
        this.loyaltyLoading = false;
      }
    },

    loyaltyTypeBadgeClass(type) {
      return {
        EARN: 'badge-primary',
        REDEEM: 'badge-danger',
        ADJUSTMENT: 'badge-dark',
        REVERSAL: 'badge-danger',
        EXPIRE: 'badge-secondary',
      }[type] || 'badge-secondary';
    },

    openAdjustModal() {
      this.adjustForm = { delta: '', reason: '', loading: false, error: '' };
      this.$refs.adjustLoyaltyModal.show();
    },

    async submitLoyaltyAdjust() {
      this.adjustForm.loading = true;
      this.adjustForm.error = '';
      try {
        const { data } = await axios.post(`/api/loyalty/members/${this.customer_data.id}/adjust`, {
          delta: this.adjustForm.delta,
          reason: this.adjustForm.reason,
        });
        this.$refs.adjustLoyaltyModal.hide();
        this.$root.$toasts.push({ type: 'success', message: `Points adjusted. New balance: ${data.transaction.balance_after}` });
        await this.loadLoyaltyData();
      } catch (e) {
        this.adjustForm.error = e.response?.data?.message || 'Failed to adjust points.';
      } finally {
        this.adjustForm.loading = false;
      }
    },

    openMessageModal() {
      this.messageForm = { mode: 'message', text: '', loading: false };
      this.$refs.message_modal.show();
    },

    openStatementModal() {
      this.messageForm = { mode: 'statement', text: this.defaultStatementMessage, loading: false };
      this.$refs.message_modal.show();
    },

    resetMessageToStatement() {
      this.messageForm.text = this.defaultStatementMessage;
    },

    sendMessageViaSMS() {
      this.messageForm.loading = true;
      axios.post('/api/send-sms-to-customer', {
        customer: { phone: this.customer_data.phone },
        message: this.messageForm.text
      }).then(() => {
        this.$root.$toasts.push({ type: 'success', message: 'Message sent successfully' });
        this.$refs.message_modal.hide();
      }).catch(() => {
        this.$root.$toasts.push({ type: 'error', message: 'Failed to send SMS' });
      }).finally(() => {
        this.messageForm.loading = false;
      });
    },

    sendMessageViaWhatsApp() {
      if (!this.formattedWhatsappPhone) {
        this.$root.$toasts.push({ type: 'error', message: 'Customer has no phone number on file' });
        return;
      }
      const text = encodeURIComponent(this.messageForm.text);
      window.open(`https://wa.me/${this.formattedWhatsappPhone}?text=${text}`, '_blank');
      this.$refs.message_modal.hide();
    },

    openMergeModal() {
      this.$refs.mergeModal.show();
    },

    resetMerge() {
      clearTimeout(this.mergeForm.timer);
      this.mergeForm = { search: '', results: [], target: null, counts: {}, loadingPreview: false, merging: false, timer: null };
    },

    searchMergeCandidates() {
      clearTimeout(this.mergeForm.timer);
      const term = this.mergeForm.search.trim();
      if (term.length < 2) {
        this.mergeForm.results = [];
        return;
      }
      this.mergeForm.timer = setTimeout(() => {
        axios.get('/api/allcustomers', { params: { search: term, perPage: 20 } })
          .then(({ data }) => {
            this.mergeForm.results = (data.data || []).filter(c => c.id !== this.customer_data.id);
          });
      }, 300);
    },

    selectMergeTarget(customer) {
      this.mergeForm.target = customer;
      this.mergeForm.counts = {};
      this.mergeForm.loadingPreview = true;
      axios.get('/api/customers/merge-preview', { params: { keep_id: this.customer_data.id, merge_id: customer.id } })
        .then(({ data }) => { this.mergeForm.counts = data.counts || {}; })
        .catch(error => {
          this.mergeForm.target = null;
          this.$root.$toasts.push({ type: 'error', message: error.response?.data?.message || 'Could not load merge preview' });
        })
        .finally(() => { this.mergeForm.loadingPreview = false; });
    },

    confirmMerge() {
      this.mergeForm.merging = true;
      axios.post('/api/customers/merge', { keep_id: this.customer_data.id, merge_id: this.mergeForm.target.id })
        .then(({ data }) => {
          this.$root.$toasts.push({ type: 'success', message: data.message });
          // Wallet, loyalty and history panels all change; reload them together.
          setTimeout(() => window.location.reload(), 800);
        })
        .catch(error => {
          this.$root.$toasts.push({ type: 'error', message: error.response?.data?.message || 'Merge failed' });
        })
        .finally(() => { this.mergeForm.merging = false; });
    },

    async DeleteCustomer(customer_id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this customer? This cannot be undone.',
        { title: 'Delete Customer', okVariant: 'danger', okTitle: 'Delete', centered: true }
      );
      if (!confirmed) return;

      axios.get('/api/customer_delete', { params: { customer_id } })
        .then(() => {
          this.$root.$toasts.push({ type: 'success', message: 'Customer data has been deleted' });
          window.location.href = '/customers';
        })
        .catch(() => {
          this.$root.$toasts.push({ type: 'error', message: 'Failed to delete customer' });
        });
    },

    clearFilter() {
      this.dateRange = { startDate: null, endDate: null };
      this.getcustomer_data();
    },

    async downloadCustomerReport(customer_id) {
      try {
        const response = await axios.get('/api/customer-transaction-reports', {
          params: {
            customer_id,
            start_date: this.dateRange.startDate || undefined,
            end_date: this.dateRange.endDate || undefined,
          },
          responseType: 'blob'
        });

        if (response.status === 200) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          const fileName = `Report-${moment().format('YYYY-MM-DD-HH-mm')}.pdf`;
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        } else {
          this.$root.$toasts.push({ type: 'error', message: 'Error downloading report' });
        }
      } catch (error) {
        console.error(error);
        this.$root.$toasts.push({ type: 'error', message: 'Error downloading report' });
      }
    },

    EditCustomerInfo(e) {
      e.preventDefault();
      axios.post('/api/updatecustomer', { ...this.customer_data })
        .then(result => {
          if (result.status === 200) {
            this.$root.$toasts.push({ type: 'success', message: "Customer data updated successfully" });
            this.$refs['edit-customer-modal'].hide();
          } else {
            this.$root.$toasts.push({ type: 'error', message: "There was an error adding customer" });
          }
        })
        .catch(error => {
          this.$root.$toasts.push({ type: 'error', message: error.response?.data?.message || 'Could not update the customer.' });
        });
    },

    hasPermission(permissionName) {
      return (this.userDetails?.permissions || []).some(permission => permission.name === permissionName);
    },

    formatDate(date, format) {
      return moment(date).format(format);
    },

    EditCustomerModal() {
      this.$refs['edit-customer-modal'].show();
    },
  }
};
</script>
